import { createTheme, responsiveFontSizes, Theme } from '@mui/material/styles';
import { getDesignTokens } from '../theme/tokens';
import { glassSx } from '../theme/glass';

export const getTheme = (mode: 'light' | 'dark' = 'light'): Theme => {
  const designTokens = getDesignTokens(mode);
  const { colors, typography, radii, animations, layout } = designTokens;
  const glass2 = glassSx(designTokens, 2);
  const glass3 = glassSx(designTokens, 3);

  let theme = createTheme({
    tokens: designTokens,
    palette: {
      mode,
      primary: {
        main: colors.primary,
        light: colors.primaryLight,
        dark: colors.primaryDark,
        contrastText: colors.onPrimary,
      },
      secondary: {
        main: colors.secondary,
        light: colors.secondaryLight,
        dark: colors.secondaryDark,
        contrastText: colors.onPrimary,
      },
      success: {
        main: colors.success,
      },
      warning: {
        main: colors.warning,
      },
      error: {
        main: colors.error,
      },
      info: {
        main: colors.info,
      },
      background: {
        default: colors.backgroundDefault,
        paper: colors.backgroundPaper,
        post: colors.backgroundCard,
        transparent: 'transparent',
      },
      text: {
        primary: colors.textPrimary,
        secondary: colors.textSecondary,
      },
      divider: colors.divider,
      logo: {
        primary: colors.logoPrimary,
        secondary: colors.logoSecondary,
      },
    },
    shape: {
      borderRadius: Number.parseFloat(radii.card),
    },
    spacing: (factor: string | number) => {
      if (typeof factor === 'string' && factor in designTokens.spacing) {
        return designTokens.spacing[factor as keyof typeof designTokens.spacing];
      }
      return `${Number(factor) * Number.parseFloat(designTokens.spacing.sm)}px`;
    },
    typography: {
      fontFamily: typography.fontFamily,
      fontWeightLight: typography.weights.light,
      fontWeightRegular: typography.weights.regular,
      fontWeightMedium: typography.weights.medium,
      fontWeightBold: typography.weights.bold,
      fontWeightSemiBold: typography.weights.semiBold,
      fontWeightExtraBold: typography.weights.extraBold,

      // Custom typography variants
      micro: { fontSize: typography.sizes.micro },
      caption: { fontSize: typography.sizes.caption },
      captionLarge: { fontSize: typography.sizes.captionLarge },
      meta: { fontSize: typography.sizes.meta },
      metaLarge: { fontSize: typography.sizes.metaLarge },
      body: { fontSize: typography.sizes.body },
      bodyLarge: { fontSize: typography.sizes.bodyLarge },
      lead: { fontSize: typography.sizes.lead },
      titleLarge: { fontSize: typography.sizes.titleLarge },
      brand: { fontFamily: typography.brandFontFamily, fontSize: typography.sizes.brand },
      mobileTitle: { fontSize: typography.sizes.mobileTitle },
      bodyEmphasis: { fontSize: typography.sizes.bodyEmphasis },
      inboxEmptyIcon: { fontSize: typography.sizes.inboxEmptyIcon },
      searchEmptyIcon: { fontSize: typography.sizes.searchEmptyIcon },
      emptyStateIcon: { fontSize: typography.sizes.emptyStateIcon },
      achievementIcon: { fontSize: typography.sizes.achievementIcon },
      achievementHeroIcon: { fontSize: typography.sizes.achievementHeroIcon },

      // Standard MUI variants — headings use the Archivo Black display face (v2)
      h1: { fontFamily: typography.displayFontFamily, fontSize: typography.sizes.h1, fontWeight: typography.weights.extraBold, letterSpacing: '-0.02em' },
      h2: { fontFamily: typography.displayFontFamily, fontSize: typography.sizes.h2, fontWeight: typography.weights.extraBold, letterSpacing: '-0.01em' },
      h3: { fontFamily: typography.displayFontFamily, fontSize: typography.sizes.subtitle, fontWeight: typography.weights.bold },
      h4: { fontFamily: typography.displayFontFamily, fontSize: typography.sizes.bodyMui, fontWeight: typography.weights.bold },
      h5: { fontFamily: typography.sansFontFamily, fontSize: typography.sizes.bodySmallMui, fontWeight: typography.weights.bold },
      h6: { fontFamily: typography.displayFontFamily, fontSize: typography.sizes.h6, fontWeight: typography.weights.bold, letterSpacing: '-0.01em' },
      subtitle1: { fontFamily: typography.sansFontFamily, fontSize: typography.sizes.subtitle, fontWeight: typography.weights.semiBold },
      body1: { fontSize: typography.sizes.bodyMui, fontWeight: typography.weights.regular },
      body2: { fontSize: typography.sizes.bodySmallMui, fontWeight: typography.weights.regular },
      // Buttons use the Archivo (sans) face, bold (v2 .btn)
      button: { fontFamily: typography.sansFontFamily, fontSize: typography.sizes.bodyMui, fontWeight: typography.weights.bold },
    } as any,
    components: {
      MuiTypography: {
        styleOverrides: {
          root: {
            wordBreak: 'break-word',
          },
        },
      },
      // v3 buttons (guide .btn): pill shape, Archivo bold, press scale
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: radii.shFull,
            minHeight: layout.buttonHeight,
            padding: '12px 26px',
            fontFamily: typography.sansFontFamily,
            fontWeight: typography.weights.bold,
            fontSize: '14px',
            letterSpacing: '0.01em',
            transition: `box-shadow ${animations.durM3} ${animations.easeM3}, background-color ${animations.durM3} ${animations.easeM3}, transform 120ms ${animations.easeM3}`,
            '&:active': {
              transform: 'scale(0.97)',
            },
            '&.Mui-disabled': {
              opacity: 0.38,
            },
          },
          sizeSmall: {
            minHeight: '36px',
            padding: '8px 18px',
            fontSize: '13px',
          },
          // .btn-filled: solid accent + red glow on hover
          containedPrimary: {
            backgroundColor: colors.primary,
            color: colors.onPrimary,
            '&:hover': {
              backgroundColor: colors.primary,
              boxShadow: '0 6px 22px rgba(229,9,20,0.38)',
            },
          },
          // .btn-tonal: accent-dim fill, accent text
          containedSecondary: {
            backgroundColor: colors.redSoft,
            color: colors.primary,
            border: 'none',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: colors.redSoft,
              color: colors.primaryDark,
              boxShadow: 'none',
            },
          },
          // .btn-outline: 1.5px hairline, plain text color
          outlined: {
            border: `1.5px ${designTokens.borders.style} ${colors.glassLine2}`,
            color: colors.textPrimary,
            '&:hover': {
              border: `1.5px ${designTokens.borders.style} ${colors.glassLine2}`,
              backgroundColor: `rgba(${colors.stateLayer}, 0.08)`,
            },
          },
          // .btn-text: accent text button
          text: {
            color: colors.primary,
            padding: '12px 14px',
          },
          outlinedError: {
            borderColor: colors.redLine,
            color: colors.primary,
            '&:hover': {
              backgroundColor: colors.primary,
              borderColor: colors.primary,
              color: colors.onPrimary,
            },
          },
        },
      },
      // Guide .fab: 16px radius, accent, red halo
      MuiFab: {
        styleOverrides: {
          root: {
            borderRadius: radii.shLg,
            backgroundColor: colors.primary,
            color: colors.onPrimary,
            boxShadow: '0 10px 28px rgba(229,9,20,0.4)',
            '&:hover': {
              backgroundColor: colors.primary,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'none',
        },
        styleOverrides: {
          root: {
            '&:hover': {
              color: colors.primaryLight,
            },
            '&:active': {
              color: colors.primaryDark,
            },
          },
        },
      },
      // v3 inputs (guide .field-o): 12px radius, 1.5px hairline, accent focus border
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radii.shMd,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.glassLine2,
              borderWidth: '1.5px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.glassLine2,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary,
              borderWidth: '1.5px',
            },
          },
        },
      },
      // Shrunk labels become mono uppercase micro-labels (guide .field-o label)
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: colors.text3,
            '&.MuiInputLabel-shrink': {
              fontFamily: typography.monoFontFamily,
              fontSize: '13px',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            },
            '&.Mui-focused': {
              color: colors.primary,
            },
          },
        },
      },
      // v3 dialogs (guide .dlg): 28px radius, glass-2, scrim backdrop
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: radii.shXl,
            ...glass2,
            backgroundImage: 'none',
            boxShadow: `${colors.glassShadow2}, inset 0 1px 0 ${colors.glassSpec}`,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: colors.scrim,
            '&.MuiBackdrop-invisible': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      // Drawers (side nav / bottom sheets) are floating surfaces → glass-2
      MuiDrawer: {
        styleOverrides: {
          paper: {
            ...glass2,
            backgroundImage: 'none',
          },
        },
      },
      // Menus & popovers (guide .menu): 16px glass, 8px-radius items
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: radii.shLg,
            ...glass2,
            backgroundImage: 'none',
          },
          list: {
            padding: '7px',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: radii.shSm,
            fontSize: '13.5px',
            gap: '11px',
            '& .MuiListItemIcon-root': {
              color: colors.text3,
              minWidth: 'unset',
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: radii.shLg,
            ...glass2,
            backgroundImage: 'none',
          },
        },
      },
      // Tooltips (guide .tip)
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            ...glass3,
            color: colors.textPrimary,
            borderRadius: radii.shSm,
            padding: '8px 13px',
            fontSize: '12px',
            fontWeight: typography.weights.semiBold,
          },
        },
      },
      // Snackbar/alert (guide .snack): glass, accent action
      MuiAlert: {
        styleOverrides: {
          root: {
            ...glass3,
            color: colors.textPrimary,
            borderRadius: radii.shMd,
            fontSize: '13.5px',
            '& .MuiAlert-action .MuiButtonBase-root': {
              color: colors.primary,
            },
          },
        },
      },
      // Chips (guide .chip): 8px radius, Archivo 600, tonal selected state
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radii.shSm,
            fontFamily: typography.sansFontFamily,
            fontWeight: typography.weights.semiBold,
            fontSize: '13px',
          },
          outlined: {
            borderColor: colors.glassLine2,
            color: colors.text2,
          },
          filledPrimary: {
            backgroundColor: colors.redSoft,
            color: colors.primary,
            border: `1px ${designTokens.borders.style} ${colors.redLine}`,
            '&:hover': {
              backgroundColor: colors.redSoft,
            },
          },
        },
      },
      // Switch (guide .msw): 54×32 hairline track, growing thumb, accent when on
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 54,
            height: 32,
            padding: 0,
          },
          switchBase: {
            padding: 0,
            // Centred against the track rather than nudged with per-state top
            // margins: those margins are calibrated for one thumb size, so a
            // Switch rendered at any other size sits visibly off-centre.
            top: '50%',
            left: 5,
            margin: 0,
            transform: 'translateY(-50%)',
            '&.Mui-checked': {
              transform: 'translate(22px, -50%)',
              '& .MuiSwitch-thumb': {
                width: 22,
                height: 22,
                backgroundColor: colors.onPrimary,
              },
              '& + .MuiSwitch-track': {
                backgroundColor: colors.primary,
                borderColor: colors.primary,
                opacity: 1,
              },
            },
          },
          thumb: {
            width: 18,
            height: 18,
            backgroundColor: colors.text3,
            boxShadow: 'none',
            transition: `all ${animations.durM3} ${animations.easeM3}`,
          },
          track: {
            borderRadius: radii.shFull,
            backgroundColor: colors.backgroundCardHover,
            border: `1.5px ${designTokens.borders.style} ${colors.glassLine2}`,
            opacity: 1,
            transition: `all ${animations.durM3} ${animations.easeM3}`,
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          color: 'primary',
        },
      },
      MuiRadio: {
        defaultProps: {
          color: 'primary',
        },
      },
      // Tabs (guide .tabs): Archivo 600, 3px rounded accent indicator
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
            backgroundColor: colors.primary,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontFamily: typography.sansFontFamily,
            fontWeight: typography.weights.semiBold,
            fontSize: '13.5px',
            color: colors.text3,
            '&.Mui-selected': {
              color: colors.primary,
            },
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          label: {
            fontFamily: typography.monoFontFamily,
            fontSize: '9.5px',
            letterSpacing: '0.06em',
            '&.Mui-selected': {
              fontSize: '9.5px',
            },
          },
        },
      },
      // Linear progress (guide .lin): 5px, hairline track, red gradient bar
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 5,
            borderRadius: 3,
            backgroundColor: colors.glassLine,
          },
          bar: {
            borderRadius: 3,
            background: 'linear-gradient(90deg, #B20710, #E50914)',
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          rail: {
            backgroundColor: colors.glassLine2,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--font-body': typography.fontFamily,
            '--font-display': typography.displayFontFamily,
            '--font-sans': typography.sansFontFamily,
            '--font-mono': typography.monoFontFamily,
            '--color-text-2': colors.text2,
            '--color-text-3': colors.text3,
            '--color-text-4': colors.text4,
            '--red-soft': colors.redSoft,
            '--red-line': colors.redLine,
            '--radius-none': radii.none,
            '--radius-control': radii.control,
            '--radius-image': radii.image,
            '--radius-card': radii.card,
            // v3 re-points: legacy radius vars now land on the M3 shape scale
            '--radius-large': radii.shLg,
            '--radius-card-large': radii.shXl,
            '--radius-card-xlarge': radii.shXl,
            '--radius-input': radii.shMd,
            '--radius-pill': radii.pill,
            '--radius-circle': radii.circle,
            '--sh-xs': radii.shXs,
            '--sh-sm': radii.shSm,
            '--sh-md': radii.shMd,
            '--sh-lg': radii.shLg,
            '--sh-xl': radii.shXl,
            '--sh-full': radii.shFull,
            '--border-thin-solid': `${designTokens.borders.widthThin} ${designTokens.borders.style}`,
            '--space-none': designTokens.spacing.none,
            '--space-inline-tight': designTokens.spacing.inlineTight,
            '--space-inline-compact': designTokens.spacing.inlineCompact,
            '--space-inline': designTokens.spacing.inline,
            '--space-inline-relaxed': designTokens.spacing.inlineRelaxed,
            '--space-stack-tight': designTokens.spacing.stackTight,
            '--space-stack-compact': designTokens.spacing.stackCompact,
            '--space-stack': designTokens.spacing.stack,
            '--space-stack-relaxed': designTokens.spacing.stackRelaxed,
            '--space-stack-normal': designTokens.spacing.stackNormal,
            '--space-stack-large': designTokens.spacing.stackLarge,
            '--space-stack-xlarge': designTokens.spacing.stackXLarge,
            '--space-form-switch-gap': designTokens.spacing.formSwitchGap,
            '--space-form-submit-gap': designTokens.spacing.formSubmitGap,
            '--space-nested-comment-indent': designTokens.spacing.nestedCommentIndent,
            '--space-loading-offset': designTokens.spacing.loadingOffset,
            '--space-empty-state-offset': designTokens.spacing.emptyStateOffset,
            '--space-empty-state-top-offset': designTokens.spacing.emptyStateTopOffset,
            '--space-section-inline-margin': designTokens.spacing.sectionInlineMargin,
            '--space-section-vertical-padding': designTokens.spacing.sectionVerticalPadding,
            '--space-field-control-gap': designTokens.spacing.fieldControlGap,
            '--space-icon-gap': designTokens.spacing.iconGap,
            '--space-icon-gap-negative': `-${designTokens.spacing.iconGap}`,
            '--space-sm': designTokens.spacing.sm,
            '--layout-page-min-height': layout.pageMinHeight,
            '--layout-viewport-height': layout.viewportHeight,
            '--layout-compact-avatar': layout.compactAvatar,
            '--layout-avatar': layout.avatar,
            '--layout-vote-rail-width': layout.voteRailWidth,
            '--layout-thumbnail': layout.thumbnail,
            '--layout-settings-row-height': layout.settingsRowHeight,
            '--layout-compact-control': layout.compactControl,
            '--layout-image-preview': layout.imagePreview,
            '--layout-top-bar-height': layout.topBarHeight,
            '--glass-background': colors.glassBackground,
            '--glass-border': colors.glassBorder,
            '--glass-blur': designTokens.effects.glassBlur,
            // v3 glass recipe + accent + motion vars
            '--g-bg': colors.glass1,
            '--g-bg-2': colors.glass2,
            '--g-bg-3': colors.glass3,
            '--g-line': colors.glassLine,
            '--g-line-2': colors.glassLine2,
            '--g-spec': colors.glassSpec,
            '--g-shadow': colors.glassShadow,
            '--g-shadow-2': colors.glassShadow2,
            '--scrim': colors.scrim,
            '--accent': colors.primary,
            '--accent-dim': colors.redSoft,
            '--accent-line': colors.redLine,
            '--ease-m3': animations.easeM3,
            '--dur-m3': animations.durM3,
          },
          body: {
            transition: `background-color ${animations.slow} ${animations.easing}, color ${animations.slow} ${animations.easing}`,
          },
          // Theme-switch smoothing on major surfaces only — a universal `*`
          // transition makes every hover/paint animate and janks the aurora.
          '.MuiPaper-root, .MuiAppBar-root': {
            transition: `background-color ${animations.slow} ${animations.easing}, border-color ${animations.slow} ${animations.easing}`,
          },
          code: {
            fontFamily: typography.codeFontFamily,
          },
          a: {
            textDecoration: 'none',
          },
          'a:hover': {
            color: colors.primaryLight,
          },
          'a:active': {
            color: colors.primaryDark,
          },
          '::selection': {
            backgroundColor: colors.primary,
            color: colors.onPrimary,
          },
        },
      },
    },
    breakpoints: {
      values: layout.breakpoints,
    },
  });

  try {
    theme = responsiveFontSizes(theme);
  } catch (err) {
    try {
      console.error('responsiveFontSizes error', err);
    } catch (e) {}
    throw err;
  }
  return theme;
};

export const colorTokens = {
  background: {
    dark: getDesignTokens('dark').colors.backgroundDefault,
  },
};

export default colorTokens;
